import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type ReactNode } from "react";

import { rememberOrganization } from "@/auth/organization";
import { Button, cx, ErrorAlert, FieldLabel, Form, Icon, Spinner, useFormField } from "@/components/ui";
import { identity } from "@/lib/api";
import type { Organization } from "@/lib/types";

const SECTORS = ["UK Education", "UK Real Estate", "Professional Services", "Other"] as const;
const GOALS = [
  "Launch a website",
  "Get more enquiries",
  "Set up professional email",
  "Find a domain",
] as const;
const MAX_SERVICES = 5;

/**
 * Figma "Add a new business" card. Identity stores a business (organization) by name only today,
 * so that is what "Create business" saves; the profile answers (what it does, sector, services,
 * goals) are collected for the design and will be stored once the portal API has a business
 * profile.
 */
export function NewBusinessForm({
  title = "Add a new business",
  titleId: callerTitleId,
  onCreated,
}: {
  title?: string;
  /** The heading's id, for a dialog that is named by it; unique per instance when omitted. */
  titleId?: string;
  onCreated: (org: Organization) => void;
}) {
  const generatedTitleId = useId();
  const titleId = callerTitleId ?? generatedTitleId;
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sector, setSector] = useState<(typeof SECTORS)[number] | null>(null);
  const [otherSector, setOtherSector] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);

  const create = useMutation({
    mutationFn: () => identity.createOrganization(name.trim()),
    onSuccess: async (org) => {
      rememberOrganization(org.id);
      await qc.invalidateQueries();
      onCreated(org);
    },
  });

  return (
    <Form
      className="flex flex-col gap-[26px]"
      error={create.error}
      onSubmit={(e) => {
        e.preventDefault();
        if (!create.isPending) create.mutate();
      }}
    >
      <div className="flex flex-col gap-2">
        <h2 id={titleId} className="text-2xl font-semibold text-fg">
          {title}
        </h2>
        <p className="text-sm text-fg-subtle">
          Tell us the basics. You can change them later. You don’t need a domain or a specific industry to
          start.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <ErrorAlert error={create.error} />
        <TextField
          label="Business name"
          name="name"
          value={name}
          onChange={setName}
          placeholder="Salt & Bloom Florals"
          required
          maxLength={100}
        />
        <TextField
          label="What does your business do?"
          value={description}
          onChange={setDescription}
          placeholder="Wedding and event flowers, plus same-day bouquets in Brighton."
          maxLength={280}
        />

        <ChipGroup label="Which sector fits best?">
          {SECTORS.map((s) => (
            <Chip
              key={s}
              selected={sector === s}
              onClick={() => setSector((v) => (v === s ? null : s))}
              role="radio"
            >
              {s}
            </Chip>
          ))}
        </ChipGroup>
        {sector === "Other" && (
          <TextField
            label="Your sector"
            value={otherSector}
            onChange={setOtherSector}
            placeholder="Florist & events"
            maxLength={80}
          />
        )}

        <ChipGroup label="Main services" hint={`Up to ${MAX_SERVICES}`}>
          {services.map((s) => (
            <Chip
              key={s}
              selected
              onClick={() => setServices((v) => v.filter((x) => x !== s))}
              ariaLabel={`Remove ${s}`}
            >
              {s}
            </Chip>
          ))}
          {services.length < MAX_SERVICES && (
            <AddService onAdd={(s) => setServices((v) => (v.includes(s) ? v : [...v, s]))} />
          )}
        </ChipGroup>

        <ChipGroup label="What do you want to do first?" hint="Pick any">
          {GOALS.map((g) => (
            <Chip
              key={g}
              selected={goals.includes(g)}
              onClick={() => setGoals((v) => (v.includes(g) ? v.filter((x) => x !== g) : [...v, g]))}
              role="checkbox"
            >
              {g}
            </Chip>
          ))}
        </ChipGroup>
      </div>

      <Button
        unstyled
        type="submit"
        disabled={create.isPending}
        aria-busy={create.isPending}
        className="flex w-full items-center justify-center gap-2 rounded-[11px] bg-brand-500 px-5 py-[13px] text-[15px] font-semibold text-white drop-shadow-[0px_4px_7px_rgba(13,8,5,0.2)] transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {create.isPending && <Spinner className="size-4 text-white" />}
        Create business
      </Button>
    </Form>
  );
}

const INPUT =
  "w-full rounded-[10px] border border-border bg-surface p-[13px] text-[15px] text-fg transition-colors placeholder:text-fg-subtle focus:border-brand-500";

function TextField({
  label,
  name,
  required,
  value,
  onChange,
  ...rest
}: {
  label: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
}) {
  const { id, message, messageId, control } = useFormField({ label, name, required });
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel htmlFor={id} required={required} className="text-[13px] font-semibold text-fg-muted">
        {label}
      </FieldLabel>
      <input
        {...control}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cx(INPUT, message && "border-red-500")}
        {...rest}
      />
      {message && (
        <p id={messageId} className="text-xs text-red-600">
          {message}
        </p>
      )}
    </div>
  );
}

function ChipGroup({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2" role="group" aria-labelledby={id}>
      <p id={id} className="flex items-center gap-1.5">
        <span className="text-[13px] font-semibold text-fg">{label}</span>
        {hint && <span className="text-xs text-fg-subtle">{hint}</span>}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

const CHIP =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-[7px] text-[12.5px] font-semibold whitespace-nowrap transition-colors";

function Chip({
  selected,
  onClick,
  role,
  ariaLabel,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  role?: "radio" | "checkbox";
  ariaLabel?: string;
  children: ReactNode;
}) {
  return (
    <Button
      unstyled
      type="button"
      role={role}
      aria-checked={role ? selected : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cx(
        CHIP,
        selected
          ? "border-brand-500 bg-[#fef3f0] text-brand-500"
          : "border-border bg-surface text-fg-muted hover:border-[#d4cac4]",
      )}
    >
      {selected && <Icon name="check" size={12} />}
      {children}
    </Button>
  );
}

/** "+ Add service" turns into an inline input; Enter (or leaving it) adds the service. */
function AddService({ onAdd }: { onAdd: (service: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const commit = () => {
    const s = value.trim();
    if (s) onAdd(s);
    setValue("");
    setEditing(false);
  };
  if (!editing) {
    return (
      <Button
        unstyled
        type="button"
        onClick={() => setEditing(true)}
        className={cx(CHIP, "border-border bg-surface text-fg-muted hover:border-[#d4cac4]")}
      >
        + Add service
      </Button>
    );
  }
  return (
    <input
      // The field appears when "+ Add service" is pressed, so focus follows the user into it.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={value}
      maxLength={40}
      placeholder="e.g. Workshops"
      aria-label="New service"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.stopPropagation();
          setValue("");
          setEditing(false);
        }
      }}
      className={cx(CHIP, "w-40 border-brand-500 bg-surface text-fg placeholder:font-normal")}
    />
  );
}
